import axios from 'axios';
import { handleLogout, httpServices } from './client';
import type { UploadOutcome, UploadResponse } from './library';

/**
 * One chat attachment upload (`POST /llm/chat/document/{id}/add_attachments`,
 * documents-api.md §2), one file per request so each file has its own
 * progress, Cancel and Retry. The server validates the content too and
 * answers with a per-file outcome.
 */

export type AttachmentUpload =
  | { kind: 'done'; outcome: UploadOutcome }
  | { kind: 'cancelled' }
  /** No response arrived: Retry may work. */
  | { kind: 'network' }
  | { kind: 'error'; status: number; message: string | null; code?: string };

/** Uploads wait this long for the server before they count as lost. */
const UPLOAD_TIMEOUT_MS = 10 * 60_000;

export const uploadAttachment = async (
  inquiryId: string,
  file: File,
  options: {
    onProgress?: (percent: number) => void;
    signal?: AbortSignal;
  } = {},
): Promise<AttachmentUpload> => {
  const form = new FormData();
  form.append('files[]', file);
  try {
    const response = await httpServices.llmService.axiosInstance.post<
      UploadResponse & { message?: string }
    >(`/chat/document/${inquiryId}/add_attachments`, form, {
      signal: options.signal,
      timeout: UPLOAD_TIMEOUT_MS,
      // Every answer is read here: a 422 still lists the file's outcome.
      validateStatus: () => true,
      onUploadProgress: event => {
        if (!options.onProgress) return;
        const total = event.total ?? file.size;
        options.onProgress(
          total > 0
            ? Math.min(100, Math.round((event.loaded / total) * 100))
            : 0,
        );
      },
    });
    if (response.status === 401) handleLogout('expired');
    const body = response.data;
    const outcome = body?.results?.[0];
    if (outcome && (response.status === 200 || response.status === 422)) {
      return { kind: 'done', outcome };
    }
    return {
      kind: 'error',
      status: response.status,
      message: body?.message ?? body?.error ?? null,
      code: body?.code,
    };
  } catch (error) {
    if (axios.isCancel(error) || options.signal?.aborted) {
      return { kind: 'cancelled' };
    }
    return { kind: 'network' };
  }
};
